import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  cn,
  ConfirmModal,
  Icon,
  Input,
  Modal,
  TextInput,
  Virtualizer,
} from 'corso-admin-ui';
import { FormEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import EmptyStateAction from '~/components/EmptyStateAction';

type TagLike = { id: number; name: string };

type TagCreateValues = { name: string };

type TagsSettingsLabels = {
  // Singular noun for the entity, e.g. "Claim", "Registration".
  entitySingular: string;
  // Plural noun for the entity, e.g. "Claims", "Registrations".
  entityPlural: string;
};

type UseTagCreateHook = () => {
  mutate: (name: string) => void;
};

type UseTagDeleteHook<T extends TagLike> = () => {
  mutate: (id: T['id']) => void;
  isPending: boolean;
};

function DeleteTag<T extends TagLike>({
  tag,
  labels,
  useTagDelete,
}: {
  tag: T;
  labels: TagsSettingsLabels;
  useTagDelete: UseTagDeleteHook<T>;
}) {
  const { mutate, isPending } = useTagDelete();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const confirmDelete = () => {
    mutate(tag.id);
    setShowConfirmation(false);
  };

  return (
    <>
      <Action
        icon="remove"
        accessibilityLabel={`Delete ${tag.name}`}
        loading={isPending}
        onClick={() => setShowConfirmation(true)}
      />

      <ConfirmModal
        title={`Delete ${labels.entitySingular} Tag`}
        prompt={`Are you sure? Tag will be removed from all ${labels.entityPlural} and Automation Rules. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmation}
        loading={isPending}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function TagForm({
  show,
  onClose,
  labels,
  useTagCreate,
  createSchema,
}: {
  show: boolean;
  onClose: () => void;
  labels: TagsSettingsLabels;
  useTagCreate: UseTagCreateHook;
  createSchema: z.ZodType<TagCreateValues, TagCreateValues>;
}) {
  const {
    formState: { errors },
    handleSubmit,
    reset,
    register,
  } = useForm<TagCreateValues>({
    resolver: zodResolver(createSchema),
  });

  const { mutate: createTag } = useTagCreate();

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      createTag(formValues.name);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal
      title={`Create ${labels.entitySingular} Tag`}
      show={show}
      onClose={onClose}
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          details="The name of the tag."
          required
          {...register('name')}
          error={errors.name?.message}
        />

        <Action variant="primary" type="submit">
          Create
        </Action>
      </form>
    </Modal>
  );
}

export function TagsSettingsCard<T extends TagLike>({
  labels,
  useTags,
  useTagCreate,
  useTagDelete,
  createSchema,
  showForm,
  setShowForm,
}: {
  labels: TagsSettingsLabels;
  useTags: () => { data: T[] | undefined; isLoading: boolean };
  useTagCreate: UseTagCreateHook;
  useTagDelete: UseTagDeleteHook<T>;
  createSchema: z.ZodType<TagCreateValues, TagCreateValues>;
  showForm: boolean;
  setShowForm: (show: boolean) => void;
}) {
  const { data: tags = [], isLoading } = useTags();
  const [filteredTags, setFilteredTags] = useState(tags);

  useEffect(() => {
    setFilteredTags(tags);
  }, [tags]);

  const tagsExist = tags.length > 0 && !isLoading;
  const entitySingularLower = labels.entitySingular.toLowerCase();
  const entityPluralLower = labels.entityPlural.toLowerCase();

  return (
    <Card>
      {tagsExist ? (
        <Input
          label="Search tags"
          id="search-tags"
          type="search"
          labelVisuallyHidden
          placeholder="Search tags"
          onChange={(e) => {
            const search = e.target.value.toLowerCase();
            setFilteredTags(
              tags.filter((t) => t.name.toLowerCase().includes(search)),
            );
          }}
        />
      ) : (
        <EmptyStateAction.Button
          onClick={() => setShowForm(true)}
          icon={<Icon icon="tags" />}
          label={`Create a ${entitySingularLower} tag`}
          description={`${labels.entitySingular} tags allow you to categorize and filter ${entityPluralLower}. ${labels.entitySingular} tags can also be created when managing Automation Rules or ${labels.entityPlural}.`}
        />
      )}

      <Virtualizer
        items={filteredTags}
        estimateSize={() => 50}
        visibleCount={50}
      >
        {(tag, index) => (
          <div
            key={tag.id}
            className={cn(
              'group flex items-center justify-between gap-2 border-b py-2',
              index === 0 && 'pt-0',
              index === filteredTags.length - 1 && 'border-0 pb-0',
            )}
          >
            <Badge>{tag.name}</Badge>
            <DeleteTag tag={tag} labels={labels} useTagDelete={useTagDelete} />
          </div>
        )}
      </Virtualizer>

      <TagForm
        show={showForm}
        onClose={() => setShowForm(false)}
        labels={labels}
        useTagCreate={useTagCreate}
        createSchema={createSchema}
      />
    </Card>
  );
}
